import React, { useRef, useState } from 'react'
import { ImagePlus, Loader2, MoreHorizontal, Pencil, Plus } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { brandsApi } from '@/lib/api/brands'
import {
  useDeleteProductMutation,
  useGetBrandCatalogQuery,
  useSaveProductMutation,
  type CatalogBrand,
} from '@/redux/marketerApi'
import { SettingsCard } from './SettingsCard'

type Product = CatalogBrand['products'][number]
const field =
  'h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground outline-none focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40'
const COLORS = ['#0EA5E9', '#9333EA', '#16A34A', '#F97316', '#2563EB', '#DB2777']

const Tile: React.FC<{ src?: string | null; name: string; size?: string; i?: number }> = ({
  src,
  name,
  size = 'h-10 w-10 text-sm',
  i = 0,
}) =>
  src ? (
    <img src={src} alt="" className={`${size} shrink-0 rounded-lg object-cover`} />
  ) : (
    <span
      className={`${size} flex shrink-0 items-center justify-center rounded-lg font-bold`}
      style={{ background: COLORS[i % COLORS.length], color: '#fff' }}
    >
      {name.slice(0, 2).toUpperCase()}
    </span>
  )

async function upload(file: File) {
  const ext = file.type.split('/')[1] || 'png'
  const name = `brand-${Date.now()}.${ext}`
  const { uploadUrl, publicUrl } = (await brandsApi.getUploadUrl(name, file.type)).data
  await brandsApi.uploadFile(uploadUrl, file)
  return publicUrl
}

/** Name and picture, for a brand or a product */
const EditDialog: React.FC<{
  open: boolean
  onOpenChange: (o: boolean) => void
  title: string
  initial: { name: string; image: string | null; category?: string | null }
  withCategory?: boolean
  onSave: (v: { name: string; image: string | null; category: string }) => Promise<void>
}> = ({ open, onOpenChange, title, initial, withCategory, onSave }) => {
  const { toast } = useToast()
  const [name, setName] = useState(initial.name)
  const [category, setCategory] = useState(initial.category ?? '')
  const [image, setImage] = useState<string | null>(initial.image)
  const [busy, setBusy] = useState(false)
  const file = useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (open) {
      setName(initial.name)
      setCategory(initial.category ?? '')
      setImage(initial.image)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-border bg-card sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Athletes see this name and picture.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <Tile src={image} name={name || '?'} size="h-14 w-14 text-lg" />
          <button
            type="button"
            onClick={() => file.current?.click()}
            disabled={busy}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
          >
            <ImagePlus className="h-4 w-4" /> {image ? 'Change picture' : 'Add a picture'}
          </button>
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0]
              e.target.value = ''
              if (!f) return
              setBusy(true)
              try {
                setImage(await upload(f))
              } catch {
                toast({ title: "Couldn't upload the picture", variant: 'destructive' })
              } finally {
                setBusy(false)
              }
            }}
          />
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          aria-label="Name"
          maxLength={120}
          className={field}
        />
        {withCategory ? (
          <input
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="What it is, e.g. Sports drink"
            aria-label="Category"
            maxLength={120}
            className={field}
          />
        ) : null}
        <button
          type="button"
          disabled={busy || !name.trim()}
          onClick={async () => {
            setBusy(true)
            try {
              await onSave({ name: name.trim(), image, category: category.trim() })
              onOpenChange(false)
            } finally {
              setBusy(false)
            }
          }}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save
        </button>
      </DialogContent>
    </Dialog>
  )
}

type Editing =
  | { kind: 'brand'; brand: CatalogBrand | null }
  | { kind: 'product'; brand: CatalogBrand; product: Product | null }
  | null

/** Settings > Brands: the company's brands and the products under each */
export const BrandsTab: React.FC = () => {
  const { toast } = useToast()
  const { data, refetch, isLoading } = useGetBrandCatalogQuery()
  const [saveProduct] = useSaveProductMutation()
  const [removeProduct] = useDeleteProductMutation()
  const [editing, setEditing] = useState<Editing>(null)

  const fail = (err: any) =>
    toast({
      title: "Couldn't save",
      description: err?.data?.error ?? err?.response?.data?.error ?? 'Try again.',
      variant: 'destructive',
    })

  const saveBrand = async (
    brand: CatalogBrand | null,
    v: { name: string; image: string | null },
  ) => {
    try {
      if (brand)
        await brandsApi.updateBrand(brand.id, { name: v.name, logo: v.image ?? undefined })
      else await brandsApi.createBrand({ name: v.name, logo: v.image ?? undefined } as any)
      await refetch()
      toast({ title: brand ? 'Brand updated' : 'Brand added' })
    } catch (err) {
      fail(err)
      throw err
    }
  }

  const deleteBrand = async (brand: CatalogBrand) => {
    try {
      await brandsApi.deleteBrand(brand.id)
      await refetch()
      toast({ title: `${brand.name} removed` })
    } catch (err: any) {
      toast({
        title: "Couldn't remove the brand",
        description: err?.response?.data?.error ?? 'Brands with campaigns can’t be removed.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="max-w-4xl">
      <SettingsCard
        title="Your brands"
        subtitle="Manage your brands and the products under each brand."
        action={
          <button
            type="button"
            onClick={() => setEditing({ kind: 'brand', brand: null })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-[#C0FD72] px-4 text-sm font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
          >
            <Plus className="h-4 w-4" /> Add brand
          </button>
        }
      >
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : null}
        <div className="space-y-4">
          {(data?.brands ?? []).map((b, bi) => (
            <section key={b.id} className="rounded-xl border border-border">
              <div className="flex flex-wrap items-center gap-3 p-4">
                <Tile src={b.logo} name={b.name} size="h-12 w-12 text-base" i={bi} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-lg font-bold text-foreground">{b.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {b.products.length} product{b.products.length === 1 ? '' : 's'} · {b.campaigns}{' '}
                    campaign
                    {b.campaigns === 1 ? '' : 's'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditing({ kind: 'brand', brand: b })}
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit brand
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    aria-label={`${b.name} actions`}
                    className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => setEditing({ kind: 'product', brand: b, product: null })}
                    >
                      Add a product
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => deleteBrand(b)} className="text-[#DC2626]">
                      Remove brand
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="border-t border-border px-4 pb-3 pt-3">
                <p className="mb-2 text-sm font-semibold text-foreground">Products</p>
                {b.products.length ? (
                  <ul className="divide-y divide-border">
                    {b.products.map((p, pi) => (
                      <li key={p.id} className="flex flex-wrap items-center gap-3 py-2.5">
                        <Tile src={p.imageUrl} name={p.name} i={bi + pi + 1} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-foreground">{p.name}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {p.category ?? 'Product'}
                          </p>
                        </div>
                        <span className="w-28 text-sm text-muted-foreground">
                          {p.campaigns} campaign{p.campaigns === 1 ? '' : 's'}
                        </span>
                        <button
                          type="button"
                          onClick={() => setEditing({ kind: 'product', brand: b, product: p })}
                          className="h-9 rounded-lg border border-border px-3 text-sm font-medium text-foreground hover:bg-muted"
                        >
                          Edit
                        </button>
                        <DropdownMenu>
                          <DropdownMenuTrigger
                            aria-label={`${p.name} actions`}
                            className="rounded-lg border border-border p-2 text-muted-foreground hover:bg-muted"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-[#DC2626]"
                              onSelect={async () => {
                                try {
                                  await removeProduct({ brandId: b.id, productId: p.id }).unwrap()
                                  toast({ title: `${p.name} removed` })
                                } catch (err) {
                                  fail(err)
                                }
                              }}
                            >
                              Remove product
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No products yet.</p>
                )}
                <button
                  type="button"
                  onClick={() => setEditing({ kind: 'product', brand: b, product: null })}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3F7A0A] hover:underline"
                >
                  <Plus className="h-4 w-4" /> Add product
                </button>
              </div>
            </section>
          ))}
        </div>
      </SettingsCard>

      <EditDialog
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        title={
          editing?.kind === 'brand'
            ? editing.brand
              ? 'Edit brand'
              : 'Add a brand'
            : editing?.product
              ? 'Edit product'
              : `Add a product to ${editing?.brand.name ?? ''}`
        }
        withCategory={editing?.kind === 'product'}
        initial={
          editing?.kind === 'brand'
            ? { name: editing.brand?.name ?? '', image: editing.brand?.logo ?? null }
            : {
                name: editing?.product?.name ?? '',
                image: editing?.product?.imageUrl ?? null,
                category: editing?.product?.category ?? '',
              }
        }
        onSave={async (v) => {
          if (!editing) return
          if (editing.kind === 'brand') return saveBrand(editing.brand, v)
          try {
            await saveProduct({
              brandId: editing.brand.id,
              productId: editing.product?.id,
              name: v.name,
              category: v.category || undefined,
              imageUrl: v.image ?? undefined,
              description: editing.product?.description ?? undefined,
            }).unwrap()
            toast({ title: editing.product ? 'Product updated' : 'Product added' })
          } catch (err) {
            fail(err)
            throw err
          }
        }}
      />
    </div>
  )
}

export default BrandsTab
